import { supabase } from "@/lib/supabase";
import type { ExerciseRow } from "@/types/database";

export interface ListExercisesFilters {
  is_active?: boolean;
  search?: string;
  video_id?: string;
}

export async function listExercises(filters?: ListExercisesFilters): Promise<ExerciseRow[]> {
  let q = supabase.from("exercises").select("*").order("created_at", { ascending: false });
  if (typeof filters?.is_active === "boolean") q = q.eq("is_active", filters.is_active);
  if (filters?.video_id) q = q.eq("video_id", filters.video_id);
  if (filters?.search?.trim()) q = q.ilike("title", `%${filters.search.trim()}%`);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as ExerciseRow[];
}

export async function getExercise(id: string): Promise<ExerciseRow | null> {
  const { data, error } = await supabase.from("exercises").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return (data ?? null) as ExerciseRow | null;
}

export interface CreateExerciseInput {
  title: string;
  description: string;
  duration_seconds: number | null;
  video_id: string | null;
  thumbnail_url: string | null;
  is_active: boolean;
  created_by: string | null;
}

export async function createExercise(input: CreateExerciseInput): Promise<ExerciseRow> {
  const { data, error } = await supabase
    .from("exercises")
    .insert(input as never)
    .select()
    .single();
  if (error) throw error;
  return data as ExerciseRow;
}

export async function createExercises(inputs: CreateExerciseInput[]): Promise<ExerciseRow[]> {
  if (inputs.length === 0) return [];
  const { data, error } = await supabase
    .from("exercises")
    .insert(inputs as never)
    .select();
  if (error) throw error;
  return (data ?? []) as ExerciseRow[];
}

export async function updateExercise(
  id: string,
  patch: Partial<Pick<ExerciseRow, "title" | "description" | "duration_seconds" | "video_id" | "thumbnail_url" | "is_active">>,
): Promise<ExerciseRow> {
  const { data, error } = await supabase
    .from("exercises")
    .update(patch as never)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as ExerciseRow;
}

export async function deleteExercise(id: string): Promise<void> {
  const { error } = await supabase.from("exercises").delete().eq("id", id);
  if (error) throw error;
}
